import styled from 'styled-components';
import { v2Colors, v2FontFamily, v2Breakpoints } from '../../../../formInputsV2/styles/tokens';

// v2 look for the Auto/Moto/Commercial "guarantee slot" cards — the grid of
// per-guarantee panels on the result page itself (not inside a modal) that
// hold the title, the selected quote (or a placeholder to pick one), and the
// Aggiungi/Rimuovi row. Shared by RcQuotes, RcMotoQuotes and RcCommercialQuotes
// via ButtonComponentLogic(Prima) and SelectedQuotesCards(Prima).
//
// RiskCard's three visual states are driven entirely by the Bootstrap utility
// classNames the existing `riskCardStyles`/`riskCardPrimaStyles` helpers
// already build (RcQuotesModal.style.js) — logic untouched, only what those
// classes look like changes here:
//   (none)                        - open, nothing chosen yet
//   .bg-secondary.bg-opacity-25   - locked (pick the main guarantee first)
//   .bg-success.bg-opacity-25     - filled (a quote is selected for this slot)
export const RiskContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: ${v2Breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

export const RiskCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;
  padding: 18px 20px !important;
  background: ${v2Colors.inputBackground};
  border: 1px solid ${v2Colors.disabledBorder};
  border-radius: 16px !important;
  font-family: ${v2FontFamily};
  transition: background-color 150ms ease, border-color 150ms ease;

  /* The title wrapper (its direct-child div, holding the h5 below) used to
     carry its own Bootstrap padding utilities (p-3, p-1 p-lg-3, ...) —
     dropped from every product's JSX in favour of this, so there's one place
     that controls it: no left padding (this card's own padding above already
     clears the left edge), 8px top/right/bottom on desktop, 4px below the
     tablet breakpoint. The extra margin-bottom only widens the gap below the
     title, on top of this card's own flex "gap" above — the card-to-buttons
     gap further down stays as it was. */
  > div:first-child {
    padding: 8px 8px 8px 0;
    margin-bottom: 4px;
  }

  /* Scoped to the slot's own title (its direct-child div) — a bare "h5"
     here would also match the "Prezzo annuo:"/details labels buried inside
     the selected-quote card nested below, since CSS specificity doesn't
     care about nesting depth, only the selector itself */
  > div > h5 {
    margin: 0;
    color: ${v2Colors.text};
    font-family: ${v2FontFamily};
    font-size: 16.5px;
    font-weight: 600;
    line-height: 1.3;
  }

  /* Locked: nothing to do here yet, so it recedes */
  &.bg-secondary.bg-opacity-25 {
    background: ${v2Colors.surface} !important;
    border-style: dashed;
    opacity: 1 !important;

    h5 {
      color: ${v2Colors.textMuted};
      font-weight: 500;
    }
  }

  /* Filled: a quote is selected for this slot */
  &.bg-success.bg-opacity-25 {
    background: ${v2Colors.primarySoft} !important;
    border-color: ${v2Colors.primaryBorder};
    opacity: 1 !important;
  }

  /* The wrapper every product's JSX puts around the selected-quote card
     (QuotationCardResult/RiskCardSelected) — grow it to fill whatever's left
     of this slot's height (this card is already grid-stretched to match its
     tallest sibling in the row) and stretch the card itself to fill that, so
     a guarantee with little detail (e.g. Tutela Legale) reads the same
     height as one with a lot (e.g. Responsabilita Civile) instead of only
     being as tall as its own content. */
  > .col-12.mb-2 {
    display: flex;
    flex: 1 1 auto;
  }

  > .col-12.mb-2 > * {
    flex: 1 1 auto;
    width: 100%;
  }

  @media (max-width: ${v2Breakpoints.tablet}) {
    padding: 14px 16px !important;

    > div:first-child {
      padding: 4px 4px 4px 0;
      margin-bottom: 2px;
    }
  }
`;

// "Valore Veicolo: X €" — replaces the plain Bootstrap `alert alert-info`
// pill shown next to the title for the vehicle-value guarantee types
// (furto_incendio, eventi_naturali, eventi_socio_politici).
export const VehicleValueBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 6px;
  background: ${v2Colors.infoSoft};
  font-family: ${v2FontFamily};
  white-space: nowrap;

  small {
    font-size: 12px;
    font-weight: 600;
    color: ${v2Colors.info};
  }
`;

// "Aggiungi" — combined with Bootstrap's .btn.btn-success in the JSX; this
// overrides that green to the app's own brand green and drops the outline
// look the old (bg/color commented out) definition left it with.
export const RiskButton = styled.button`
  padding: 8px 20px !important;
  border: 1px solid ${v2Colors.primary} !important;
  border-radius: 10px !important;
  background: ${v2Colors.primary} !important;
  color: #fff !important;
  font-family: ${v2FontFamily};
  font-size: 13.5px;
  font-weight: 600;
  transition: background-color 150ms ease, border-color 150ms ease;

  &:hover {
    border-color: ${v2Colors.primaryHover} !important;
    background: ${v2Colors.primaryHover} !important;
  }

  &:focus-visible {
    outline: 0;
    box-shadow: 0 0 0 3px ${v2Colors.primarySoftHover};
  }
`;

// "Rimuovi" / "Cambia" — tonal red, matching the Chiudi button in the modals.
export const RiskButtonRemove = styled.button`
  padding: 8px 20px !important;
  border: 1px solid ${v2Colors.dangerBorder} !important;
  border-radius: 10px !important;
  background: ${v2Colors.dangerSoft} !important;
  color: ${v2Colors.danger} !important;
  font-family: ${v2FontFamily};
  font-size: 13.5px;
  font-weight: 600;
  transition: background-color 150ms ease, border-color 150ms ease;

  &:hover {
    border-color: ${v2Colors.danger} !important;
    background: ${v2Colors.dangerSoftHover} !important;
  }

  &:focus-visible {
    outline: 0;
    box-shadow: 0 0 0 3px ${v2Colors.dangerSoftHover};
  }
`;

// "Seleziona Prima La Responsibilita Civile" — replaces the plain Bootstrap
// `alert alert-warning`, same tonal-amber language as the semestrale warning
// in the v2 RiskCard.
export const SelectFirstNotice = styled.div`
  padding: 7px 12px;
  border: 1px solid ${v2Colors.warningBorder};
  border-radius: 10px;
  background: ${v2Colors.warningSoft};
  color: ${v2Colors.warningText};
  font-family: ${v2FontFamily};
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.3;
`;

// "Seleziona Quotazione!" — replaces the plain Bootstrap `alert
// alert-secondary` clickable placeholder that opens the guarantee's modal.
export const SelectQuotePlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px;
  border: 1.5px dashed ${v2Colors.border};
  border-radius: 12px;
  color: ${v2Colors.textMuted};
  font-family: ${v2FontFamily};
  font-size: 13.5px;
  font-weight: 600;
  text-align: center;
  cursor: pointer;
  transition: border-color 150ms ease, color 150ms ease, background-color 150ms ease;

  &:hover {
    border-color: ${v2Colors.primary};
    background: ${v2Colors.primarySoft};
    color: ${v2Colors.primaryHover};
  }
`;
